import React from 'react';
import { connect } from 'react-redux';
import { GridToolbar } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Field } from '@progress/kendo-react-form';
import { provideLocalizationService, provideIntlService, registerForLocalization, registerForIntl } from '@progress/kendo-react-intl';
import { DateTimeFormatter, EmptyFormatter, Grid, InlineBadge } from '../../shared/components';
import { getGeneralCompetencyStatusThemeColor } from '../../../resources/generalCompetencyStatus';
import { TextBox } from '../../../ui';
import FormDialog from '../../../ui/components/application/form-dialog';
import { findMyHours, sortMyHours, toggleEnterCodeDialog, submitCode } from '../actions/my-hours-list-page-actions';

class MyHoursList extends React.PureComponent {
  constructor() {
    super();
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleSort = this.handleSort.bind(this);
    this.handleEnterCodeClick = this.handleEnterCodeClick.bind(this);
    this.handleEnterCodeCancel = this.handleEnterCodeCancel.bind(this);
    this.handleEnterCodeSubmit = this.handleEnterCodeSubmit.bind(this);
    this.handleEnterCodeValidate = this.handleEnterCodeValidate.bind(this);
  }

  render() {
    const localizationService = provideLocalizationService(this);
    const intlService = provideIntlService(this);
    return (
      <>
        <Grid
          loading={this.props.loading}
          data={this.props.hours}
          skip={this.props.skip}
          take={this.props.take}
          total={this.props.total}
          sort={this.props.sorting}
          onSortChange={this.handleSort}
          onPageChange={this.handlePageChange}
          columns={[
            {
              field: 'sessionName',
              className: 'align-left',
              headerClassName: 'align-left',
              title: localizationService.toLanguageString('myGeneralCompetencyHours.sessionName'),
              sortable: false,
              cell: (e) => (
                <td className={e.className}>
                  {e.dataItem.eventName
                    ? `${e.dataItem.sessionName} (${e.dataItem.eventName})`
                    : e.dataItem.sessionName}
                </td>
              )
            },
            {
              field: 'occurredAt',
              title: localizationService.toLanguageString('myGeneralCompetencyHours.occurredAt'),
              width: '180px',
              cell: (e) => (
                <td>{EmptyFormatter(DateTimeFormatter(e.dataItem.occurredAt, intlService))}</td>
              )
            },
            {
              field: 'minutes',
              title: localizationService.toLanguageString('myGeneralCompetencyHours.minutesColumn'),
              width: '120px'
            },
            {
              field: 'sourceType',
              className: 'align-left',
              headerClassName: 'align-left',
              title: localizationService.toLanguageString('myGeneralCompetencyHours.sourceType'),
              sortable: false,
              width: '200px'
            },
            {
              field: 'status',
              title: localizationService.toLanguageString('myGeneralCompetencyHours.status'),
              sortable: false,
              width: '160px',
              cell: (e) => this.renderStatus(e)
            }
          ]}
        >
          <GridToolbar>
            <Button themeColor='primary' onClick={this.handleEnterCodeClick}>
              {localizationService.toLanguageString('myGeneralCompetencyHours.enterCode')}
            </Button>
          </GridToolbar>
        </Grid>
        {this.props.isEnterCodeDialogVisible &&
          <FormDialog
            title={localizationService.toLanguageString('myGeneralCompetencyHours.enterCodeDialogTitle')}
            onSubmit={this.handleEnterCodeSubmit}
            onCancel={this.handleEnterCodeCancel}
            validator={this.handleEnterCodeValidate}
            validationResult={this.props.alreadyRegistered
              ? { errorMessage: localizationService.toLanguageString('myGeneralCompetencyHours.alreadyRegistered') }
              : this.props.enterCodeValidationResult
            }
            loading={this.props.submittingCode}
            confirmButtonText={localizationService.toLanguageString('myGeneralCompetencyHours.enterCodeSubmit')}
            closeButtonText={localizationService.toLanguageString('buttons.cancel')}
            width={400}
            render={() => (
              <Field
                name='code'
                component={TextBox}
                label={localizationService.toLanguageString('myGeneralCompetencyHours.enterCodeLabel')}
              />
            )}
          />
        }
      </>
    );
  }

  renderStatus(e) {
    const { status, statusCode } = e.dataItem;
    return (
      <td className={e.className}>
        <InlineBadge themeColor={getGeneralCompetencyStatusThemeColor(statusCode)}>
          {status}
        </InlineBadge>
      </td>
    );
  }

  async handlePageChange(e) {
    await this.props.findMyHours({ skip: e.page.skip, take: e.page.take });
  }

  handleSort(e) {
    this.props.sortMyHours(e.sort);
  }

  handleEnterCodeClick() {
    this.props.toggleEnterCodeDialog(true);
  }

  handleEnterCodeCancel() {
    this.props.toggleEnterCodeDialog(false);
  }

  handleEnterCodeSubmit(values) {
    this.props.submitCode({ code: values.code });
  }

  handleEnterCodeValidate(values, localizationService) {
    const errors = {};
    if (!values.code || !values.code.trim()) {
      errors.code = localizationService.toLanguageString('validation.required');
    }
    return errors;
  }
}

const mapStateToProps = state => ({
  loading: state.myGeneralCompetencyHoursListPage.loading,
  hours: state.myGeneralCompetencyHoursListPage.hours,
  skip: state.myGeneralCompetencyHoursListPage.skip,
  take: state.myGeneralCompetencyHoursListPage.take,
  total: state.myGeneralCompetencyHoursListPage.total,
  sorting: state.myGeneralCompetencyHoursListPage.sorting,
  isEnterCodeDialogVisible: state.myGeneralCompetencyHoursListPage.isEnterCodeDialogVisible,
  submittingCode: state.myGeneralCompetencyHoursListPage.submittingCode,
  alreadyRegistered: state.myGeneralCompetencyHoursListPage.alreadyRegistered,
  enterCodeValidationResult: state.myGeneralCompetencyHoursListPage.enterCodeValidationResult
});

const mapDispatchToProps = dispatch => ({
  findMyHours: (payload) => dispatch(findMyHours(payload)),
  sortMyHours: (payload) => dispatch(sortMyHours(payload)),
  toggleEnterCodeDialog: (isVisible) => dispatch(toggleEnterCodeDialog(isVisible)),
  submitCode: (payload) => dispatch(submitCode(payload))
});

registerForLocalization(MyHoursList);
registerForIntl(MyHoursList);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MyHoursList);
